import './ForcedOnModal.scss';

import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';

import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import ForcedOnIcon from '@mui/icons-material/OfflineBolt';
import DrawingIcon from '@mui/icons-material/DescriptionOutlined';
import SaveIcon from '@mui/icons-material/SaveRounded';
import { Map } from 'immutable';
import humps from 'humps';
import { useFormik } from 'formik';
import yup from 'src/utils/yup';

import Button from 'src/component/UI/Button';
import DeviceSelectorField from 'src/component/UI/DeviceSelectorField';
import PermissionFence from 'src/component/PermissionFence';
import FeatureFence from 'src/component/FeatureFence';
import ForcedOnSafetyCheck from 'src/component/UI/ForcedOnCircuits/ForcedOnSafetyCheck';
import { getSingularWorkTicketLabel, getClient, getTemperatureUnit } from 'src/module/authentication/selector';
import { getDeviceById, getDeviceFiles } from 'src/module/device/selector';
import { fetchDevice, fetchDeviceFiles, fetchDeviceFile } from 'src/module/device/action';
import { useTranslation } from 'src/i18n';
import { evaluateSetpointSafety } from 'src/utils/miSetpointCalculator';
import { celsiusToFahrenheitConvertor } from 'src/utils/utils';

const MAX_REMOVAL_DAYS = 180;
export const CONTROL_MODES = { freeze_protection: 'forcedOn.freezeProtection', process_maintain: 'forcedOn.processMaintain' };

export default function ForcedOnModal (props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    handleClose,
    open,
    modalTitle,
    circuitId,
    forcedOnCircuit,
    readOnlyCircuit,
    readonly
  } = props;
  const [archived, setArchived] = useState(false);
  const [pnid, setPnid] = useState('---');
  const [lineNumber, setLineNumber] = useState('---');
  const [_circuitId, setCircuitId] = useState(circuitId === null ? forcedOnCircuit?.get('circuit_id') : circuitId);
  const applyToCircuit = useRef(false);
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);
  const dispatch = useDispatch();

  // MI setpoint safety check, gated behind a facility preference.
  const client: any = useSelector(getClient);
  const validateSetpointCheck = client?.getIn(['preferences', 'validateCheckWhenCreatingForcingOnLogs'], false);
  const safetyFactor = client?.getIn(['preferences', 'safetyFactor'], 40);
  // Only a new log can be applied to the circuit; editing an existing log stays log-only.
  const showLogAndApply = validateSetpointCheck && !forcedOnCircuit;
  const temperatureUnit = useSelector(getTemperatureUnit);
  const device: any = useSelector(getDeviceById(_circuitId));

  const hasSafetyTemps = device?.get('max_sheath_temp') !== undefined && device?.get('auto_ignition_temp') !== undefined;
  // The circuit's live control temperature set point, stored in °C. Undefined until the device is fetched.
  const circuitSetPoint = device?.getIn(['settings', 'controlTemperatureSetpoint']);

  const circuitSetPointValue = circuitSetPoint === undefined || circuitSetPoint === null ? ''
    : String(temperatureUnit === 'F' ? celsiusToFahrenheitConvertor(circuitSetPoint) : circuitSetPoint);

  const fetchedCircuitId = useRef<string | null>(null);
  useEffect(() => {
    const needsFetch = (!forcedOnCircuit && circuitSetPoint === undefined) || (validateSetpointCheck && !hasSafetyTemps);
    if (open && _circuitId && needsFetch && fetchedCircuitId.current !== _circuitId) {
      fetchedCircuitId.current = _circuitId;
      dispatch(fetchDevice(_circuitId));
    }
  }, [open, forcedOnCircuit, validateSetpointCheck, _circuitId, hasSafetyTemps, circuitSetPoint]);

  const drawings = useSelector(getDeviceFiles(_circuitId), shallowEqual).filter((file: any) => file.sub_type === 'Drawing');
  useEffect(() => {
    if (open && _circuitId) {
      dispatch(fetchDeviceFiles(_circuitId));
    }
  }, [open, _circuitId]);

  // A new request counts from today; an existing log counts from when it was created.
  const requestDate = forcedOnCircuit?.get('created_at') ? moment(forcedOnCircuit.get('created_at')) : moment();
  const maxRemovalDate = requestDate.clone().add(MAX_REMOVAL_DAYS, 'days').format('YYYY-MM-DD');

  // Logs saved before the dropdown hold free text; keep showing that log's own value as an option.
  const controlModeOptions = (savedMode) => [
    <MenuItem key='' value=''>—</MenuItem>,
    ...Object.entries(CONTROL_MODES).map(([mode, labelKey]) => <MenuItem key={mode} value={mode}>{t(labelKey)}</MenuItem>),
    ...(savedMode && !CONTROL_MODES[savedMode] ? [<MenuItem key={savedMode} value={savedMode}>{savedMode}</MenuItem>] : []),
  ];

  const formik = useFormik({
    initialValues: {
      circuitId: _circuitId,
      personRequestingChange: '',
      reasonForRequest: '',
      rtdReading: '',
      note: '',
      notificationNumber: '',
      originalSetPoint: '',
      originalControlMode: '',
      forcedOnSetPoint: '',
      forcedOnControlMode: '',
      forcedOnAt: moment().format('YYYY-MM-DD'),
      expectedRemovalAt: moment().add(30, 'days').format('YYYY-MM-DD')
    },
    onSubmit: values => {
      const forcedOnCircuit: Record<string, unknown> = {
        circuitId: values.circuitId,
        personRequestingChange: values.personRequestingChange,
        reasonForRequest: values.reasonForRequest,
        rtdReading: values.rtdReading,
        note: values.note,
        notificationNumber: values.notificationNumber,
        originalSetPoint: values.originalSetPoint,
        originalControlMode: values.originalControlMode,
        forcedOnSetPoint: values.forcedOnSetPoint,
        forcedOnControlMode: values.forcedOnControlMode,
        forcedOnAt: moment(values.forcedOnAt, 'YYYY-MM-DD').toDate(),
        expectedRemovalAt: moment(values.expectedRemovalAt, 'YYYY-MM-DD').toDate(),
      };
      if (applyToCircuit.current) {
        forcedOnCircuit.applyToCircuit = true;
        forcedOnCircuit.forcedOnSetPoint = String(safetyCheck?.forcedOnSetPoint);
      }
      handleSubmit(Map(humps.decamelizeKeys(forcedOnCircuit)));
    },
    validationSchema: () => {
      return yup.object().shape({
        circuitId: yup
          .string(t('freezeLogs.circuit'))
          .required(t('forcedOn.circuitRequired')),
        personRequestingChange: yup
          .string(t('forcedOn.personRequesting')),
        reasonForRequest: yup
          .string(t('forcedOn.reasonForRequest')),
        rtdReading: yup
          .string(t('forcedOn.rtdReading')),
        originalSetPoint: yup
          .string(t('forcedOn.originalSetPoint')),
        originalControlMode: yup
          .string(t('forcedOn.originalControlMode')),
        forcedOnSetPoint: yup
          .string(t('forcedOn.setPoint')),
        forcedOnControlMode: yup
          .string(t('forcedOn.forceOnControlMode')),
        forcedOnAt: yup
          .string(t('forcedOn.dateChangeMade')),
        expectedRemovalAt: yup
          .string(t('forcedOn.expectedRemoval'))
          .required(t('forcedOn.expectedRemovalRequired'))
          .test('max-removal-date', t('forcedOn.expectedRemovalMax', { days: MAX_REMOVAL_DAYS }),
            (value) => {
              if (!value) return true;
              const date = moment(value, 'YYYY-MM-DD', true);
              return date.isValid() && date.isSameOrBefore(maxRemovalDate);
            }),
        note: yup
          .string(t('notes.note')),
        notificationNumber: yup
          .string(t('forcedOn.ticketNumber', { label: singularWorkTicketLabel || t('workTickets.singular') })),
      });
    },
  });

  const safetyCheck = validateSetpointCheck ? evaluateSetpointSafety({
    maxSheathTemp: device?.get('max_sheath_temp'),
    autoIgnitionTemp: device?.get('auto_ignition_temp'),
    originalSetPoint: formik.values.originalSetPoint,
    forcedOnSetPoint: formik.values.forcedOnSetPoint,
    safetyFactor,
    temperatureUnit,
  }) : undefined;
  const maxSetPointCelsius = safetyCheck?.maxSetPoint;
  const safetyCheckPassed = safetyCheck?.status === 'pass';
  // Why Log and apply to circuit is disabled, shown as its tooltip; null when it can be used.
  let applyBlockedBy = null;
  if (device?.get('offline')) {
    applyBlockedBy = 'forcedOn.applyBlockedCircuitOffline';
  } else if (!safetyCheckPassed) {
    applyBlockedBy = 'forcedOn.applyBlockedSafetyCheck';
  }
  const closeButton = <Button onClick={handleClose}>{t('common.close')}</Button>;
  // Rounded down, so a set point just under the hint still passes the check.
  const maxSetPoint = maxSetPointCelsius === undefined ? null
    : Math.floor((temperatureUnit === 'F' ? maxSetPointCelsius * 9 / 5 + 32 : maxSetPointCelsius) * 10) / 10;
  const maxSetPointHint = maxSetPoint === null ? undefined : t('forcedOn.maxSetPointHint', { max: maxSetPoint, unit: temperatureUnit });

  const handleChangeCircuitId = (value) => {
    if ( value?.id && value?.id.trim() !== '' ) {
      formik.setFieldValue('circuitId', value.id, false);
      setCircuitId(value.id);
    }
  };

  const clearValues = () => {
    formik.resetForm();
    setCircuitId(null);
  };

  // this use effect watches to see if a forced on circuit is passed and updates form
  useEffect (() => {
    if (forcedOnCircuit) {
      const foCircuit = forcedOnCircuit;
      const values: any = {};
      //update circuit id
      handleChangeCircuitId({ id: foCircuit.getIn(['circuit_id']) });
      //update formik values
      values.circuitId = foCircuit.getIn(['circuit_id']);
      values.expectedRemovalAt = foCircuit.get('expected_removal_at') ? moment(foCircuit.get('expected_removal_at')).format('YYYY-MM-DD') : '';
      values.forcedOnAt = moment(foCircuit.get('forced_on_at')).format('YYYY-MM-DD');
      values.forcedOnControlMode = foCircuit.get('forced_on_control_mode') ?? '';
      values.forcedOnSetPoint = foCircuit.get('forced_on_set_point');
      values.originalControlMode = foCircuit.get('original_control_mode') ?? '';
      values.originalSetPoint = foCircuit.get('original_set_point');
      values.rtdReading = foCircuit.get('rtd_reading');
      values.personRequestingChange = foCircuit.get('person_requesting_change');
      values.reasonForRequest = foCircuit.get('reason_for_request');
      values.note = foCircuit.get('note') !== null ? foCircuit.get('note') : '';
      values.notificationNumber = foCircuit.get('notification_number') !== null ? foCircuit.get('notification_number') :  '';
      formik.setValues(values);

      //update values pulled from circuit (not used by formik)
      setLineNumber(foCircuit.getIn(['circuit', 'line']) ? foCircuit.getIn(['circuit', 'line']) : '---');
      setPnid(foCircuit.getIn(['circuit', 'pnid']) ? foCircuit.getIn(['circuit', 'pnid']) : '---');
      setArchived(foCircuit.get('archived_at') ? true : false);
    }
    // don't clear values if circuit is readonly
    if (!forcedOnCircuit && !readOnlyCircuit) {
      clearValues();
    }
  }, [forcedOnCircuit]);

  // A new request's Original Set Point starts from the circuit's set point. It is reset when the modal opens or
  // the circuit changes, and a set point that loads later only fills the field if it is still empty.
  const prefilledCircuitId = useRef<string | null>(null);
  useEffect(() => {
    if (!open || forcedOnCircuit) {
      prefilledCircuitId.current = null;
    } else if (prefilledCircuitId.current !== _circuitId) {
      prefilledCircuitId.current = _circuitId;
      formik.setFieldValue('originalSetPoint', circuitSetPointValue, false);
    } else if (formik.values.originalSetPoint === '') {
      formik.setFieldValue('originalSetPoint', circuitSetPointValue, false);
    }
  }, [open, _circuitId, circuitSetPoint]);

  useEffect(() => {
    if (circuitId) {
      handleChangeCircuitId({ id: circuitId });
    }
    else {
      handleChangeCircuitId({ id: _circuitId });
    }
  }, [_circuitId, circuitId]);

  // this will clear the formik values when the component is unmounted
  // to make sure we're not persisting the state of the last forcedOnCircuit
  useEffect(() => {
    return clearValues;
  }, []);

  const circuitInformationHeaderTable = <table className='circuit-information-subheading'>
    <tr>
      {modalTitle.includes(t('forcedOn.editForcedOnForCircuit')) && forcedOnCircuit.get('circuit_deleted_at') !== null ? <td className='deleted-info'>{t('forcedOn.circuitDeleted')}</td> : null}
      <td>{t('forcedOn.requestId').toUpperCase()}: {forcedOnCircuit?.get('request_id')}</td>
      <td>{t('forcedOn.pnid')} {pnid}</td>
      <td>{t('forcedOn.lineNumber').toUpperCase()}: {lineNumber}</td>
    </tr>
  </table>;
  const circuitInformationHeader = forcedOnCircuit ? circuitInformationHeaderTable : null;

  return (
    <Modal
      data-testid='forced-on-modal'
      open={open}
      onClose={handleClose}
      aria-labelledby='forced-on-modal-title'
    >
      <div className='modal forced-on-modal'>
        <h2 id='forced-on-modal-title'>{modalTitle}</h2>
        <form noValidate autoComplete='off' onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {circuitInformationHeader}

            <Grid item xs={6}>
              <DeviceSelectorField deviceId={_circuitId} value={forcedOnCircuit ? { label: `${forcedOnCircuit.get('controller_tag')} / ${forcedOnCircuit.get('circuit_tag')}`, value: _circuitId } : null} readonly={readonly || readOnlyCircuit} required
                onChange={handleChangeCircuitId} error={Boolean(formik.errors.circuitId)} helperText={formik.errors.circuitId} type='circuit' />
              <TextField disabled={readonly} id='notificationNumber' name='notificationNumber' value={formik.values.notificationNumber} label={t('forcedOn.ticketNumber', { label: singularWorkTicketLabel || t('workTickets.singular') })}
                onChange={formik.handleChange} error={Boolean(formik.errors.notificationNumber)} helperText={formik.errors.notificationNumber} inputProps={{ maxLength: 255 }} InputLabelProps={{ shrink: false }} />
              <TextField disabled={readonly} id='personRequestingChange' name='personRequestingChange' value={formik.values.personRequestingChange} label={t('forcedOn.personRequesting')}
                onChange={formik.handleChange} error={Boolean(formik.errors.personRequestingChange)} helperText={formik.errors.personRequestingChange} inputProps={{ maxLength: 64 }} InputLabelProps={{ shrink: false }} />
              <TextField disabled={readonly} id='reasonForRequest' name='reasonForRequest' value={formik.values.reasonForRequest} label={t('forcedOn.reasonForRequest')}
                onChange={formik.handleChange} error={Boolean(formik.errors.reasonForRequest)} helperText={formik.errors.reasonForRequest} inputProps={{ maxLength: 255 }} InputLabelProps={{ shrink: false }} />
              <TextField disabled={readonly} id='rtdReading' name='rtdReading' value={formik.values.rtdReading} label={t('forcedOn.rtdReading')}
                onChange={formik.handleChange} error={Boolean(formik.errors.rtdReading)} helperText={formik.errors.rtdReading} inputProps={{ maxLength: 16 }} InputLabelProps={{ shrink: false }} />
              <TextField disabled={readonly} id='note' name='note' value={formik.values.note} label={t('notes.note')}
                onChange={formik.handleChange} error={Boolean(formik.errors.note)} helperText={formik.errors.note} inputProps={{ maxLength: 255 }} InputLabelProps={{ shrink: false }} />
              {drawings.length > 0 && (
                <div className='forced-on-drawings'>
                  <InputLabel variant='outlined'>{t('forcedOn.drawings')}</InputLabel>
                  {drawings.map((file: any) => (
                    <button key={file.id} type='button' className='forced-on-drawing-link'
                      onClick={() => dispatch(fetchDeviceFile(file.name, file.created_at, _circuitId, file.id))}>
                      <DrawingIcon className='forced-on-drawing-icon' />
                      <span>{file.description || file.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </Grid>
            <Grid item xs={6} className='forced-on-modal-right-column'>
              <TextField fullWidth disabled={readonly} id='originalSetPoint' name='originalSetPoint' value={formik.values.originalSetPoint} label={`${t('forcedOn.originalSetPoint')}${t('forcedOn.setPointUnit', { unit: temperatureUnit })}`}
                onChange={formik.handleChange} error={Boolean(formik.errors.originalSetPoint)} helperText={formik.errors.originalSetPoint} inputProps={{ maxLength: 16 }} InputLabelProps={{ shrink: false }} />
              <TextField select fullWidth disabled={readonly} id='originalControlMode' name='originalControlMode' value={formik.values.originalControlMode} label={t('forcedOn.originalControlMode')}
                onChange={formik.handleChange} error={Boolean(formik.errors.originalControlMode)} helperText={formik.errors.originalControlMode} InputLabelProps={{ shrink: false }}>
                {controlModeOptions(forcedOnCircuit?.get('original_control_mode'))}
              </TextField>
              <TextField fullWidth  disabled={readonly} id='forcedOnSetPoint' name='forcedOnSetPoint' value={formik.values.forcedOnSetPoint} label={`${t('forcedOn.setPoint')}${t('forcedOn.setPointUnit', { unit: temperatureUnit })}`}
                onChange={formik.handleChange} error={Boolean(formik.errors.forcedOnSetPoint)} helperText={formik.errors.forcedOnSetPoint || maxSetPointHint} inputProps={{ maxLength: 16 }} InputLabelProps={{ shrink: false }} />
              <TextField select fullWidth disabled={readonly} id='forcedOnControlMode' name='forcedOnControlMode' value={formik.values.forcedOnControlMode} label={t('forcedOn.forceOnControlMode')}
                onChange={formik.handleChange} error={Boolean(formik.errors.forcedOnControlMode)} helperText={formik.errors.forcedOnControlMode} InputLabelProps={{ shrink: false }}>
                {controlModeOptions(forcedOnCircuit?.get('forced_on_control_mode'))}
              </TextField>
              <TextField fullWidth disabled={readonly} id='forcedOnAt' name='forcedOnAt' value={formik.values.forcedOnAt} label={t('forcedOn.dateChangeMade')} type='date'
                onChange={formik.handleChange} error={Boolean(formik.errors.forcedOnAt)} helperText={formik.errors.forcedOnAt} InputLabelProps={{ shrink: false }} />
              <TextField fullWidth disabled={readonly} id='expectedRemovalAt' name='expectedRemovalAt' value={formik.values.expectedRemovalAt} label={t('forcedOn.expectedRemoval')} type='date'
                onChange={formik.handleChange} error={Boolean(formik.errors.expectedRemovalAt)} helperText={formik.errors.expectedRemovalAt} required inputProps={{ max: maxRemovalDate }} InputLabelProps={{ shrink: false }} />
            </Grid>
            {validateSetpointCheck && (
              <Grid item xs={12}>
                <ForcedOnSafetyCheck
                  maxSheathTemp={device?.get('max_sheath_temp')}
                  autoIgnitionTemp={device?.get('auto_ignition_temp')}
                  originalSetPoint={formik.values.originalSetPoint}
                  forcedOnSetPoint={formik.values.forcedOnSetPoint}
                  safetyFactor={safetyFactor}
                  temperatureUnit={temperatureUnit}
                  offersApply={showLogAndApply}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <div className='button-bar'>
                <PermissionFence can={['edit-forced-on-circuit', 'edit-device']} noPermissionComponent={closeButton}>
                  <FeatureFence can={['forced-on-logs']} noPermissionComponent={closeButton}>
                    <>
                      <Button onClick={handleClose}>{t('common.cancel')}</Button>
                      {showLogAndApply && (
                        // a disabled button fires no hover events, so the tooltip hangs off a wrapper
                        <Tooltip title={applyBlockedBy ? t(applyBlockedBy) : ''}>
                          <span className='forced-on-apply-tooltip-target'>
                            <Button disabled={archived || Boolean(applyBlockedBy)} onClick={() => {
                              applyToCircuit.current = true;
                              formik.submitForm().finally(() => { applyToCircuit.current = false; });
                            }} icon={<ForcedOnIcon style={{ fontSize: 14 }} />} border className='forced-on-apply-button'>{t('forcedOn.logAndApplyToCircuit')}</Button>
                          </span>
                        </Tooltip>
                      )}
                      <Button type='submit' disabled={archived} icon={<SaveIcon style={{ fontSize: 14 }} />} cta>{forcedOnCircuit ? t('common.save') : t('forcedOn.createLog')}</Button>
                    </>
                  </FeatureFence>
                </PermissionFence>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    </Modal>
  );
}

ForcedOnModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  modalTitle: PropTypes.string.isRequired,
  circuitId: PropTypes.string,
  forcedOnCircuit: PropTypes.object,
  readOnlyCircuit: PropTypes.bool,
  readonly: PropTypes.bool
};

ForcedOnModal.defaultProps = {
  circuitId: null,
  forcedOnCircuit: null,
  readOnlyCircuit: false,
  readonly: false
};
